import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTr } from "@/i18n/translator";
import { useAuth } from "@/context/AuthContext";
import {
  Briefcase, Plus, Check, Calendar, Plug, BarChart3, Image as ImageIcon,
  Radar, Archive, Zap, ArrowRight, Star, Wand2,
} from "lucide-react";

export default function BrandsOverview() {
  const tr = useTr();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activating, setActivating] = useState(null);

  const load = async () => {
    const { data } = await api.get("/brands/overview");
    setData(data);
  };

  useEffect(() => { load(); }, []);

  const activate = async (brandId) => {
    setActivating(brandId);
    try {
      await api.post("/brands/activate", { brand_id: brandId });
      toast.success(tr("Marca activa cambiada"));
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setActivating(null);
    }
  };

  if (!data) return <div className="font-mono-mark text-xs text-[#5C5C5C]">CARGANDO…</div>;

  const isAgency = data.plan === "agency";
  const atLimit = data.total >= data.max_brands;

  return (
    <div className="space-y-6" data-testid="brands-overview-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">
            {tr("GESTIÓN · VISTA 360°")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
            <Briefcase className="w-10 h-10 text-[#E53935]" />
            {tr("Mis marcas")}
          </h1>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-2">
            {isAgency
              ? tr("Tu plan Agencia permite hasta 5 marcas totalmente independientes.")
              : tr("Gestiona tu marca desde aquí o explora el plan Agencia para administrar múltiples.")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="font-mono-mark text-[10px] bg-[#FFD600] border border-[#0A0A0A] px-3 py-2">
            {data.total} / {data.max_brands} {tr("MARCAS")}
          </div>
          {isAgency && (
            <button
              onClick={() => nav("/dashboard/brand-builder")}
              disabled={atLimit}
              className="bg-[#E53935] text-white px-4 py-3 font-mono-mark text-xs border border-[#E53935] hover-brutal flex items-center gap-2 disabled:opacity-40"
              data-testid="new-brand-btn"
              title={atLimit ? tr("Límite alcanzado") : ""}
            >
              <Plus className="w-4 h-4" /> {tr("NUEVA MARCA")}
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {data.brands.map((b) => (
          <BrandCard
            key={b.id}
            brand={b}
            activating={activating === b.id}
            onActivate={() => activate(b.id)}
            onEdit={() => nav("/dashboard/brand")}
            onAccounts={() => nav("/dashboard/accounts")}
            onCalendar={() => nav("/dashboard/calendar")}
            onAnalytics={() => nav("/dashboard/analytics")}
            tr={tr}
          />
        ))}
        {isAgency && !atLimit && (
          <button
            onClick={() => nav("/dashboard/brand-builder")}
            className="bg-white border-2 border-dashed border-[#0A0A0A] p-8 hover:bg-[#F4F4F0] flex flex-col items-center justify-center gap-3 min-h-[380px]"
            data-testid="new-brand-card"
          >
            <Wand2 className="w-12 h-12 text-[#E53935]" />
            <div className="font-display text-xl">{tr("Crear nueva marca")}</div>
            <div className="font-mono-data text-xs text-[#5C5C5C] text-center">
              {tr("Brand Builder · logo IA, paleta, landing y ecommerce")}
            </div>
            <div className="mt-2 font-mono-mark text-[10px] bg-[#E53935] text-white px-3 py-1">
              {tr("DESDE")} $150 USD
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

function BrandCard({ brand, activating, onActivate, onEdit, onAccounts, onCalendar, onAnalytics, tr }) {
  const m = brand.metrics;
  const np = brand.next_post;

  return (
    <div
      className={`bg-white border p-0 overflow-hidden flex flex-col ${brand.is_active ? "border-[#E53935] border-2" : "border-[#0A0A0A]"}`}
      data-testid={`brand-card-${brand.id}`}
    >
      {/* Header with primary color accent */}
      <div className="p-5 border-b border-[#0A0A0A] flex items-start gap-3" style={{ backgroundColor: `${brand.primary_color}10` }}>
        {brand.logo_url ? (
          <img src={brand.logo_url} alt={brand.brand_name} className="w-14 h-14 object-contain bg-white border border-[#0A0A0A] shrink-0" />
        ) : (
          <div
            className="w-14 h-14 flex items-center justify-center text-white font-display text-2xl shrink-0"
            style={{ backgroundColor: brand.primary_color }}
          >
            {brand.brand_name?.[0] || "M"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl truncate">{brand.brand_name}</h3>
            {brand.is_active && (
              <span className="inline-flex items-center gap-1 bg-[#E53935] text-white font-mono-mark text-[9px] px-2 py-0.5">
                <Star className="w-2.5 h-2.5" /> ACTIVA
              </span>
            )}
            {brand.has_ecom && (
              <span className="bg-[#FFD600] font-mono-mark text-[9px] px-2 py-0.5">ECOM</span>
            )}
          </div>
          <div className="font-mono-data text-xs text-[#5C5C5C] mt-1 truncate">
            {brand.industry || tr("Sin industria")}
          </div>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-3 border-b border-[#0A0A0A]">
        <MetricCell label={tr("POSTS 30D")} value={m.posts_month} />
        <MetricCell label={tr("PROGRAMADOS")} value={m.posts_scheduled} border />
        <MetricCell label={tr("PUBLICADOS")} value={m.posts_published} />
      </div>
      <div className="grid grid-cols-3 border-b border-[#0A0A0A]">
        <MetricCell label={tr("REDES")} value={m.networks_connected} icon={Plug} />
        <MetricCell label={tr("ENGAGEMENT")} value={`${m.engagement_rate}%`} border icon={BarChart3} />
        <MetricCell label={tr("BANCO")} value={m.bank_items} icon={Archive} />
      </div>
      <div className="grid grid-cols-3 border-b border-[#0A0A0A]">
        <MetricCell label={tr("COMP.")} value={m.competitors} icon={Radar} />
        <MetricCell label={tr("IMÁGENES")} value={m.images} border icon={ImageIcon} />
        <MetricCell label={tr("REDES MAX")} value="5" icon={Zap} />
      </div>

      {/* Next post */}
      <div className="p-4 flex-1 bg-[#F4F4F0]">
        <div className="font-mono-mark text-[9px] text-[#5C5C5C] mb-1">{tr("PRÓXIMO POST")}</div>
        {np ? (
          <>
            <div className="font-mono-data text-xs flex items-center gap-2 mb-1">
              <Calendar className="w-3 h-3 text-[#E53935]" />
              <span className="uppercase font-mono-mark text-[9px]">{np.network}</span>
              <span className="text-[#5C5C5C]">
                {new Date(np.scheduled_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
              </span>
            </div>
            <div className="font-mono-data text-xs text-[#0A0A0A] line-clamp-2">{np.preview}…</div>
          </>
        ) : (
          <div className="font-mono-data text-xs text-[#5C5C5C]">{tr("Sin posts programados")}</div>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 border-t border-[#0A0A0A]">
        {brand.is_active ? (
          <>
            <button onClick={onEdit} className="py-3 font-mono-mark text-[10px] hover:bg-[#F4F4F0] border-r border-[#0A0A0A]" data-testid={`brand-edit-${brand.id}`}>
              {tr("EDITAR")}
            </button>
            <button onClick={onAccounts} className="py-3 font-mono-mark text-[10px] hover:bg-[#F4F4F0] flex items-center justify-center gap-1">
              {tr("GESTIONAR")} <ArrowRight className="w-3 h-3" />
            </button>
          </>
        ) : (
          <button
            onClick={onActivate}
            disabled={activating}
            className="col-span-2 py-3 bg-[#0A0A0A] text-white font-mono-mark text-[10px] hover-brutal disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid={`brand-activate-${brand.id}`}
          >
            {activating ? "ACTIVANDO…" : tr("ACTIVAR ESTA MARCA")} <Check className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function MetricCell({ label, value, border = false, icon: Icon }) {
  return (
    <div className={`p-3 text-center ${border ? "border-x border-[#0A0A0A]" : ""}`}>
      <div className="flex items-center justify-center gap-1 font-mono-mark text-[9px] text-[#5C5C5C]">
        {Icon && <Icon className="w-2.5 h-2.5" />} {label}
      </div>
      <div className="font-display text-xl mt-1">{value}</div>
    </div>
  );
}
